/**
 * useSmartPolling — auto-refresh hook that:
 *   • Polls `fetcher()` every `intervalMs` (default 30s)
 *   • Pauses automatically when the tab/window is hidden
 *     (via `document.visibilitychange`)
 *   • Resumes immediately when the user returns to the tab AND triggers
 *     an immediate refetch on focus
 *   • Exposes `lastSync` timestamp + `refresh()` manual trigger
 *   • Cleans up on unmount
 *
 * IMPORTANT: `fetcher` is stored in a ref so callers may pass a fresh
 * arrow function on every render WITHOUT retriggering the polling
 * effect. Only `enabled` and `intervalMs` drive scheduling.
 *
 * Usage:
 *   const { lastSync, refreshing, refresh } = useSmartPolling(
 *     async () => { await loadData(); },
 *     { intervalMs: 30000, enabled: true }
 *   );
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export const useSmartPolling = (fetcher, { intervalMs = 30000, enabled = true } = {}) => {
  const [lastSync, setLastSync] = useState(() => new Date());
  const [refreshing, setRefreshing] = useState(false);

  const fetcherRef = useRef(fetcher);
  const timerRef = useRef(null);
  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);

  // Always keep the latest fetcher without retriggering effects.
  useEffect(() => {
    fetcherRef.current = fetcher;
  }, [fetcher]);

  const runFetch = useCallback(async () => {
    if (!mountedRef.current) return;
    if (inFlightRef.current) return; // prevent overlap
    inFlightRef.current = true;
    try {
      setRefreshing(true);
      const fn = fetcherRef.current;
      if (typeof fn === 'function') {
        await fn();
      }
      if (mountedRef.current) setLastSync(new Date());
    } catch (err) {
      // Swallow; consumers handle their own errors.
      // eslint-disable-next-line no-console
      console.warn('[useSmartPolling] fetcher error', err);
    } finally {
      inFlightRef.current = false;
      if (mountedRef.current) setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    const clearTimer = () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };

    const startTimer = () => {
      clearTimer();
      if (!enabled) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      timerRef.current = setInterval(() => {
        if (typeof document === 'undefined' || document.visibilityState === 'visible') {
          runFetch();
        }
      }, intervalMs);
    };

    // Initial fetch on mount.
    runFetch();
    startTimer();

    const onVisibility = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        runFetch();
        startTimer();
      } else {
        clearTimer();
      }
    };

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibility);
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('focus', onVisibility);
    }

    return () => {
      mountedRef.current = false;
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibility);
      }
      if (typeof window !== 'undefined') {
        window.removeEventListener('focus', onVisibility);
      }
      clearTimer();
    };
    // We deliberately depend ONLY on enabled + intervalMs.
    // `runFetch` is stable (empty deps) and `fetcher` is read via ref.
  }, [enabled, intervalMs, runFetch]);

  return { lastSync, refreshing, refresh: runFetch };
};

export default useSmartPolling;
